import React, { useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  AddCircleOutline,
  CalendarMonthOutlined,
  CheckCircleOutline,
  CurrencyRupeeOutlined,
  PercentOutlined,
  ShieldOutlined,
  SubdirectoryArrowRightOutlined,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designShell, designType, designWeights } from "services/constants/designTokens";
import { pfRegisterHeader } from "pages/PayRoll/PayRollHeaders";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SimpleTable, ToneChip, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { RecordHeaderCard, StatutoryList, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { CODES, money, num } from "./pfReportsData";
import { pfSettings } from "./pfReportsSampleMembers";

// Contribution Register (P8) and the employee drawer (P8a): one row per PF
// member from the finalised payroll. PF Total = employee PF + extra PF +
// pension + employer PF; EDLI and admin are company costs on the challan.

const { indigo, pink, blue, green, orange, purple } = detailAccentPalette;
const NUMBER_KEYS = new Set(["unpaid_days", "epf_wage", "eps_wage", "ee_pf", "vpf", "er_eps", "er_epf", "total"]);

const ContributionPanel = ({ open, row, monthInfo, onClose }) => {
  const [tab, setTab] = useState("month");
  if (!row) return null;
  const settings = pfSettings();
  const code = CODES.find((item) => item.id === row.code_id);
  const days = moment(`${monthInfo.value}-01`).daysInMonth();
  const employerShare = row.er_eps + row.er_epf;
  const tag = row.tags[0];
  const history = [monthInfo.label, moment(`${monthInfo.value}-01`).subtract(1, "month").format("MMM YYYY"), moment(`${monthInfo.value}-01`).subtract(2, "month").format("MMM YYYY")];

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="PF Contribution"
      subtitle={`${monthInfo.label} · from finalised payroll v${monthInfo.version}`}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(row.name)}
          title={row.name}
          chip={tag ? <ToneChip label={tag.label === "full salary" ? "PF on full salary" : tag.label} tone={tag.tone} /> : null}
          meta={`${row.id} · UAN ${row.uan || "to be generated"} · ${code?.type === "MAIN" ? "Main" : "Extension"} code`}
        />
        <Mui.Tabs value={tab} onChange={(event, next) => setTab(next)} sx={{ ...designShell.panelTabs, mt: 0, borderBottom: `1px solid ${designColors.slate200}` }}>
          <Mui.Tab value="month" label="This Month" />
          <Mui.Tab value="history" label="History" />
        </Mui.Tabs>
        {tab === "month" ? (
          <>
            <RequestDetailsSection
              title="SALARY USED"
              fields={[
                { icon: CalendarMonthOutlined, label: "Days paid", value: `${days - row.unpaid_days} of ${days} · unpaid days ${row.unpaid_days}`, accent: indigo },
                {
                  icon: CurrencyRupeeOutlined,
                  label: "PF wage",
                  value: money(row.epf_wage),
                  helper:
                    row.pf_wage > settings.ceiling
                      ? row.full_salary
                        ? `Earned Basic + DA ${money(row.pf_wage)} · full salary (extra PF), not limited to ${money(settings.ceiling)}`
                        : `Earned Basic + DA ${money(row.pf_wage)} · limited to the ${money(settings.ceiling)} ceiling`
                      : `Earned Basic + DA ${money(row.pf_wage)}`,
                  accent: green,
                  multiline: true,
                },
              ]}
            />
            <RequestDetailsSection
              title="EMPLOYEE PAYS"
              fields={[
                { icon: PercentOutlined, label: `Employee PF ${Math.round(settings.eeRate * 100)}%`, value: money(row.ee_pf), accent: purple },
                ...(row.vpf ? [{ icon: AddCircleOutline, label: "Extra PF", value: money(row.vpf), accent: pink }] : []),
              ]}
            />
            <RequestDetailsSection
              title="COMPANY PAYS"
              fields={[
                { icon: PercentOutlined, label: `Employer share ${Math.round(settings.erRate * 100)}% of ${money(row.epf_wage)}`, value: money(employerShare), accent: orange },
                {
                  icon: SubdirectoryArrowRightOutlined,
                  label: `→ Pension (EPS) ${(settings.epsRate * 100).toFixed(2)}% of ${money(row.eps_wage)}`,
                  value: money(row.er_eps),
                  helper: row.eps_wage ? `Pension is always limited to ${money(settings.epsCap)}` : "Not a pension member this month",
                  accent: pink,
                },
                { icon: SubdirectoryArrowRightOutlined, label: "→ Employer PF (rest)", value: money(row.er_epf), accent: orange },
                { icon: ShieldOutlined, label: "EDLI + admin", value: `${money(row.edli)} + ${money(row.admin)}`, helper: "Company costs on the challan", accent: blue },
              ]}
            />
            <RequestDetailsSection
              title="PAYSLIP"
              fields={[{ icon: CheckCircleOutline, label: "Deducted in payslip", value: `${money(row.ee_pf + row.vpf)} · ${monthInfo.label}`, accent: green }]}
            />
          </>
        ) : (
          <Mui.Stack spacing={1.5}>
            <SimpleTable
              fit
              heads={[
                { label: "Month", width: 90 },
                { label: "PF Wage", align: "right", width: 90 },
                { label: "Employee", align: "right", width: 90 },
                { label: "Employer", align: "right", width: 90 },
                { label: "Total", align: "right", width: 90 },
              ]}
            >
              {history.map((label) => (
                <Mui.TableRow key={label}>
                  <Mui.TableCell sx={tableCellSx}>{label}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={tableCellSx}>{num(row.epf_wage)}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={tableCellSx}>{num(row.ee_pf + row.vpf)}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={tableCellSx}>{num(employerShare)}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{num(row.total)}</Mui.TableCell>
                </Mui.TableRow>
              ))}
            </SimpleTable>
            <Mui.Typography sx={designType.captionText}>Sample history: the same amounts each month until the PF APIs are connected.</Mui.Typography>
          </Mui.Stack>
        )}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const PfRegisterTab = ({ rows, monthInfo }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [viewId, setViewId] = useState(null);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pf_contribution_register_saved_views",
    fullHeaders: pfRegisterHeader,
  });
  const sum = (key) => rows.reduce((total, row) => total + row[key], 0);

  const renderCell = (row, key) => {
    if (key === "name") {
      return (
        <Mui.Stack direction="row" spacing={1} alignItems="center">
          <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>
            {initialsOf(row.name)}
          </Mui.Avatar>
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          {row.tags.map((tag) => <ToneChip key={tag.label} label={tag.label} tone={tag.tone} />)}
        </Mui.Stack>
      );
    }
    if (key === "uan_label") return row.uan || <Mui.Box component="span" sx={{ color: "#F97316" }}>To be generated</Mui.Box>;
    if (key === "vpf") return row.vpf ? num(row.vpf) : "—";
    if (key === "total") return <Mui.Box component="span" sx={{ fontWeight: designWeights.semibold }}>{num(row.total)}</Mui.Box>;
    if (NUMBER_KEYS.has(key)) return num(row[key]);
    return row[key] || "—";
  };

  return (
    <>
      <StatutoryList
        title={`PF Contribution Register ${monthInfo.label}`}
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="members"
        searchPlaceholder="Search name, ID or UAN"
        metadata={{
          "Company Name": topbarCompanyName || "N/A",
          "Payroll Month": monthInfo.label,
          "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
        }}
      />
      <Mui.Stack spacing={1.5} sx={{ mt: 1.5 }}>
        <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, textAlign: "right" }}>
          Totals: PF wages {money(sum("epf_wage"))} · employee {money(sum("ee_pf"))} · extra {money(sum("vpf"))} · employer {money(sum("er_eps") + sum("er_epf"))}
        </Mui.Typography>
        <RequestInfoBanner body="Employer PF = employer share minus pension. EDLI and admin charges are company costs paid on the challan, not shown per employee." />
      </Mui.Stack>
      <ContributionPanel open={Boolean(viewId)} row={rows.find((row) => row.id === viewId)} monthInfo={monthInfo} onClose={() => setViewId(null)} />
    </>
  );
};
